import { useState, useEffect, useCallback } from 'react';
import { Search, X, Folder, User, FileText } from 'lucide-react';
import { authFetch } from '../lib/api';

const CommandBar = ({ user, onClose, onNavigate }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ projects: [], users: [], assets: [] });
  const [loading, setLoading] = useState(false);

  const searchContent = useCallback(async () => {
    setLoading(true);
    try {
      const response = await authFetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/search?q=${encodeURIComponent(query)}`
      );

      if (response.ok) {
        const data = await response.json();
        setResults(data);
      }
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    if (query.length > 1) {
      searchContent();
    } else {
      setResults({ projects: [], users: [], assets: [] });
    }
  }, [query, searchContent]);

  const handleItemClick = (type, id) => {
    if (type === 'project') {
      onNavigate(`/project/${id}`);
    }
    onClose();
  };

  const totalResults = results.projects.length + results.users.length + results.assets.length;

  return (
    <div
      className="fixed inset-0 bg-black-stallion/20 backdrop-blur-sm z-50 flex items-start justify-center pt-32 command-bar-overlay"
      onClick={onClose}
      data-testid="command-bar-overlay"
    >
      <div
        className="w-full max-w-2xl bg-white/80 backdrop-blur-2xl border border-white shadow-2xl rounded-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="command-bar"
      >
        {/* Search Input */}
        <div className="flex items-center gap-3 px-6 py-4 border-b border-border-subtle">
          <Search className="text-brand" size={20} />
          <input
            type="text"
            placeholder="Search projects, users, assets..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="flex-1 text-xl font-medium text-text-primary bg-transparent border-none focus:ring-0 placeholder:text-text-tertiary outline-none"
            autoFocus
            data-testid="cmd-bar-input"
          />
          <button
            onClick={onClose}
            className="p-2 hover:bg-surface-page rounded-lg transition-colors"
            data-testid="cmd-bar-close"
          >
            <X size={20} className="text-text-secondary" />
          </button>
        </div>

        {/* Results */}
        <div className="max-h-96 overflow-y-auto bg-surface-page/30" data-testid="cmd-bar-results">
          {loading && (
            <div className="p-8 text-center text-text-secondary">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></div>
            </div>
          )}

          {!loading && query.length > 1 && totalResults === 0 && (
            <div className="p-8 text-center text-text-secondary">
              No results found for "{query}"
            </div>
          )}

          {!loading && totalResults > 0 && (
            <div className="p-2">
              {/* Projects */}
              {results.projects.length > 0 && (
                <div className="mb-4">
                  <div className="px-4 py-2 text-xs font-semibold text-brand uppercase tracking-wider">
                    Projects
                  </div>
                  {results.projects.map((project) => (
                    <button
                      key={project.id}
                      onClick={() => handleItemClick('project', project.id)}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/50 transition-colors search-result-item text-left"
                      data-testid={`search-result-project-${project.id}`}
                    >
                      <Folder className="text-brand" size={20} />
                      <div>
                        <p className="font-medium text-text-primary">{project.name}</p>
                        <p className="text-xs text-text-secondary">{project.status}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Users */}
              {results.users.length > 0 && (
                <div className="mb-4">
                  <div className="px-4 py-2 text-xs font-semibold text-brand uppercase tracking-wider">
                    Users
                  </div>
                  {results.users.map((searchUser) => (
                    <div
                      key={searchUser.id}
                      className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/50 transition-colors search-result-item"
                      data-testid={`search-result-user-${searchUser.id}`}
                    >
                      <User className="text-brand" size={20} />
                      <div>
                        <p className="font-medium text-text-primary">{searchUser.name}</p>
                        <p className="text-xs text-text-secondary">{searchUser.email} • {searchUser.role}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Assets */}
              {results.assets.length > 0 && (
                <div>
                  <div className="px-4 py-2 text-xs font-semibold text-brand uppercase tracking-wider">
                    Assets
                  </div>
                  {results.assets.map((asset) => (
                    <div
                      key={asset.id}
                      className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/50 transition-colors search-result-item"
                      data-testid={`search-result-asset-${asset.id}`}
                    >
                      <FileText className="text-sandy" size={20} />
                      <div>
                        <p className="font-medium text-text-primary">{asset.name}</p>
                        <p className="text-xs text-text-secondary">Status: {asset.status}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {query.length <= 1 && (
            <div className="p-8 text-center text-text-secondary text-sm">
              <p>Type to search across projects, users, and assets</p>
              <p className="mt-2 text-xs">Press <kbd className="px-2 py-1 bg-white rounded text-brand font-semibold">Esc</kbd> to close</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CommandBar;
